/**
 * The markdown a dropped tile inserts.
 *
 * A plain module rather than part of the component: these are pure functions
 * over a catalog row, every field on that row is optional, and the interesting
 * cases are the partly-filled ones - so they are worth a test that needs no
 * browser. (Node cannot type-strip .tsx, which is the other half of the reason.)
 */
/**
 * Any catalog row. Structural on purpose: this module is imported by a unit test
 * that runs under the server's tsconfig, and pulling in the browser API module
 * would drag DOM types into a project that has none.
 */
import { channelLabels } from '../../../shared/cta-channels.ts';
import { HEX, blockStyle } from '../../../shared/theme.ts';

export interface CatalogRow {
  [field: string]: unknown;
}

/** Catalog rows carry open-ended columns; only strings are safe to render. */
const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * The escaping every block below shares.
 *
 * These build HTML that is stored in the article and rendered in the editor, so
 * a clinic's own field - a doctor's name, a CTA heading - must arrive as
 * characters and never as markup. The published file escapes again from scratch
 * (server/lib/markdown.ts rebuilds each block against an allowlist), so this is
 * the first of two layers rather than the only one.
 */
const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Only an https link, or none. The same rule every stored link here follows. */
const safeUrl = (raw: string): string | null =>
  /^https:\/\//i.test(raw.trim()) ? escapeHtml(raw.trim()) : null;

/**
 * A CTA, as a box with a real button.
 *
 * It used to be `## heading` + a paragraph + a bare `[label](url)` - three
 * ordinary Markdown blocks that published as an H2 with a blue underlined link
 * in the middle of the article, indistinguishable from a citation. A CTA is the
 * one element on the page a reader is meant to ACT on, and it has to look like
 * it: its own panel, its own button, and the channels stated where somebody
 * deciding will see them.
 *
 * Written as a div with a class for the same reason the coloured boxes are:
 * Markdown has no button, the editor round-trips this class verbatim, and the
 * export rebuilds it with inline styles so it survives a WordPress theme that
 * has never heard of our stylesheet.
 */
export function ctaBlock(cta: CatalogRow): string {
  /*
   * No fallback to the name. The name is the clinic's label for the template
   * (「無料カウンセリング（LINE優先）」) and was being published as the heading
   * whenever none was written - so a clinic could not have a CTA that is just
   * a button. Blank now means blank.
   */
  const heading = escapeHtml(asText(cta.heading));
  const body = escapeHtml(asText(cta.body));
  const label = escapeHtml(asText(cta.buttonLabel) || '詳しくはこちら');
  const url = safeUrl(asText(cta.buttonUrl));
  /*
   * The labels, not the stored values.
   *
   * This published 「ご予約・ご相談：web／line」 - the catalogue screen's own
   * vocabulary, in English, in lower case, to the clinic's patients. See
   * shared/cta-channels.ts, which the prompt already read and this did not.
   */
  const channels = Array.isArray(cta.channels) ? channelLabels(cta.channels) : [];

  const parts: string[] = [];
  if (heading) parts.push(`<p class="ctabox__title">${heading}</p>`);
  if (body) parts.push(`<p>${body}</p>`);
  if (url) parts.push(`<p><a class="ctabox__button" href="${url}">${label}</a></p>`);
  // Said plainly rather than as chips: 「LINEでもご相談いただけます」 is what a
  // reader can act on, and it is still true when the styles are stripped.
  if (channels.length > 0) {
    parts.push(
      `<p class="ctabox__channels">ご予約・ご相談：${escapeHtml(channels.join('／'))}</p>`,
    );
  }
  return `<div class="ctabox">${parts.join('')}</div>`;
}

/**
 * The supervisor credit, as the card a medical article is expected to carry.
 *
 * A 監修 line is not a paragraph. It is the thing that tells a reader why the
 * page can be believed, and every clinic publishes it the same way - a
 * photograph, the name, the qualifications, the years - because that is what
 * makes it legible at a glance. It used to be `### この記事の監修` followed by a
 * bolded name and a bracketed run-on of every credential, which is the same
 * facts arranged so that nobody reads them.
 *
 * The photograph is optional and the block is built around its absence: without
 * one the card is the same card with the text where the picture would be, not a
 * broken image frame.
 */
export function supervisorBlock(supervisor: CatalogRow): string {
  const name = escapeHtml(asText(supervisor.name));
  const title = escapeHtml(asText(supervisor.title));
  const qualifications = escapeHtml(asText(supervisor.qualifications));
  const years =
    typeof supervisor.careerYears === 'number' ? `美容医療経験年数：${supervisor.careerYears}年` : '';
  const photoId = asText(supervisor.photoId);
  const profile = escapeHtml(asText(supervisor.profile));

  const parts: string[] = [];
  // The image first, so a stylesheet-free reader still meets the person before
  // the credentials - and so the card's grid has something to place.
  if (photoId) {
    parts.push(`<img class="bio__photo" src="/api/v1/images/${escapeHtml(photoId)}" alt="${name}" />`);
  } else {
    /*
     * No photograph: the initial in a circle, not a gap.
     *
     * Most clinics never upload one, and the card was then a block of text with
     * an empty column beside it - which reads as a picture that failed to load
     * rather than as a deliberate layout. The stand-in is the same one the
     * 監修医 screen shows, so what is registered there is what publishes.
     */
    parts.push(`<p class="bio__blank">${escapeHtml(asText(supervisor.name).charAt(0) || '医')}</p>`);
  }
  parts.push('<p class="bio__label">この記事の監修医師</p>');
  parts.push(`<p class="bio__name">${name}</p>`);
  if (title) parts.push(`<p class="bio__meta">${title}</p>`);
  if (qualifications) parts.push(`<p class="bio__meta">${qualifications}</p>`);
  if (years) parts.push(`<p class="bio__meta">${escapeHtml(years)}</p>`);
  if (profile) parts.push(`<p class="bio__profile">${profile}</p>`);

  return `<div class="bio">${parts.join('')}</div>`;
}

/**
 * A link the writer typed in: inline text, or a button on its own line.
 *
 * The button IS a CTA block holding nothing but its button. The node, the
 * editor's colour strip, the stylesheet and the export already know that
 * block, and a CTA with no heading is already allowed - a second kind of block
 * would be a second copy of all four. Its colour is the block's `--block` (see
 * Callout.ts); a text link's is an ordinary coloured span, the same thing the
 * toolbar's 文字色 writes.
 */
export function linkBlock(link: {
  kind: 'text' | 'button';
  label: string;
  url: string;
  color: string | null;
}): string {
  const url = safeUrl(link.url);
  if (!url) return '';
  const color = link.color && HEX.test(link.color) ? link.color.toLowerCase() : null;
  if (link.kind === 'button') {
    const label = escapeHtml(link.label.trim() || '詳しくはこちら');
    const style = color ? ` style="${blockStyle(color)}"` : '';
    return `<div class="ctabox"${style}><p><a class="ctabox__button" href="${url}">${label}</a></p></div>`;
  }
  // Brackets are the only characters that end Markdown link text early.
  const text = escapeHtml(link.label.trim() || link.url.trim()).replace(/[[\]]/g, '\\$&');
  const label = color ? `<span style="color: ${color}">${text}</span>` : text;
  return `[${label}](${link.url.trim()})`;
}
